import React, { useState, useEffect, cloneElement } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { useThemeWithUndefined } from '@rescui/ui-contexts';
import styles from '../index.p.module.css.js';
import { useMenuContext } from './menu-context.js';
const ICON_PLACEMENT_STYLES = {
  left: styles.iconPlacementLeft,
  right: styles.iconPlacementRight
};
const MODE_STYLES = {
  classic: styles.modeClassic,
  clear: styles.modeClear,
  rock: styles.modeRock
};
const SIZE_STYLES = {
  s: styles.sizeS,
  m: styles.sizeM,
  l: styles.sizeL
};
const THEME_STYLES = {
  light: styles.themeLight,
  dark: styles.themeDark
}; // eslint-disable-next-line complexity

const MenuItemInner = (_ref, ref) => {
  let {
    className,
    tag,
    children,
    icon,
    size = 'm',
    mode = 'classic',
    theme,
    disabled,
    disableCursor,
    selected,

    /* @private */
    highlighted,
    iconPlacement = 'left',
    ...restProps
  } = _ref;
  const {
    unregisterItem,
    registerItem,
    activeItemRef,
    isKeyboardNavigating,
    wrapWithLi,
    ...menuContext
  } = useMenuContext();
  const actualTheme = useThemeWithUndefined(menuContext.theme ?? theme);
  const [thisRef, setThisRef] = useState(null);
  useEffect(() => {
    return () => unregisterItem(thisRef);
  }, [unregisterItem, thisRef]);
  let autoTag = 'span';

  if (restProps.href) {
    autoTag = 'a';
  } else if (restProps.onClick) {
    autoTag = 'button';
  }

  const Tag = menuContext.itemTag ?? tag ?? autoTag;
  const actualIconPlacement = menuContext.iconPlacement ?? iconPlacement ?? 'left';
  const actualMode = menuContext.mode ?? mode;
  const actualSize = menuContext.size ?? size;
  const actualDisableCursor = menuContext.disableCursor ?? disableCursor;
  const iconElement = icon ? /*#__PURE__*/cloneElement(icon, {
    className: cn(icon.props.className, styles.icon),
    'data-render-all-sizes': true
  }) : null;
  const actualHighlighted = highlighted || activeItemRef === thisRef && isKeyboardNavigating;

  const handleRef = r => {
    registerItem(r);
    setThisRef(r);

    if (typeof ref === 'function') {
      ref(r);
    } else if (ref != null && typeof ref === 'object') {
      ref.current = r;
    }
  };

  const handleKeyDown = e => {
    if (isKeyboardNavigating && (e.key === ' ' || e.key === 'Enter')) {
      e.preventDefault();

      if (e.target && e.target.click) {
        e.target.click();
      }
    }
  };

  const Wrapper = wrapWithLi ? 'li' : 'div';
  return /*#__PURE__*/React.createElement(Wrapper, {
    className: cn(styles.wrapper, className, ICON_PLACEMENT_STYLES[actualIconPlacement], MODE_STYLES[actualMode], SIZE_STYLES[actualSize], THEME_STYLES[actualTheme]),
    "data-test": 'menu-item__wrapper',
    "data-rs-internal": 'menu__menu-item'
  }, /*#__PURE__*/React.createElement(Tag, {
    onKeyDown: handleKeyDown,
    ref: handleRef,
    className: cn(styles.menuItem, (menuContext.disabled || disabled) && styles.disabled, selected && styles.selected, actualHighlighted && styles.highlighted, actualDisableCursor && styles.disableCursor),
    "data-test": 'menu-item',
    "data-rs-internal": 'menu__menu-item__inner',
    tabIndex: 0,
    ...restProps
  }, iconElement, /*#__PURE__*/React.createElement("span", {
    className: styles.content
  }, children)));
};

const MenuItem = /*#__PURE__*/React.forwardRef(MenuItemInner);
MenuItem.propTypes = {
  size: PropTypes.oneOf(['l', 'm', 's']),
  mode: PropTypes.oneOf(['clear', 'classic', 'rock']),
  theme: PropTypes.oneOf(['light', 'dark']),
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  disableCursor: PropTypes.bool,
  selected: PropTypes.bool,

  /** @ignore */
  highlighted: PropTypes.bool,
  icon: PropTypes.element,
  iconPlacement: PropTypes.oneOf(['left', 'right']),
  tag: PropTypes.elementType
};
export { MenuItem, MenuItem as default };
